import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams, Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, ChevronRight, Clock, Trophy } from 'lucide-react';
import { getCompetition, whoami, isSignedIn, accountSummary, pastDraws } from '@/lib/api';
import { formatGBP, formatDateShort, formatNumber } from '@/lib/format';
import CountdownTimer from '@/components/comp/CountdownTimer';
import ProgressBar from '@/components/comp/ProgressBar';
import TicketSelector from '@/components/comp/TicketSelector';
import PostalEntryBox from '@/components/comp/PostalEntryBox';
import TrustStrip from '@/components/comp/TrustStrip';
import CompetitionGallery from '@/components/comp/CompetitionGallery';
import YellowRule from '@/components/brand/YellowRule';
import StickyCheckoutBar from '@/components/comp/StickyCheckoutBar';
import CheckoutSheet from '@/components/comp/CheckoutSheet';

// Resolve /api/uploads/xxx URLs (returned by our admin image upload endpoint)
// against the backend origin so uploaded hero images render regardless of
// where the backend is served. External http(s) URLs pass through.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const resolveImg = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('/api/uploads/')) return `${BACKEND_URL}${u}`;
  return u;
};

const FAQS = [
  { q: 'Is this legal?', a: 'Yes. Every competition includes a skill question and a free postal entry route — the two pillars UK prize competition law needs.' },
  { q: 'When is the draw?', a: 'The exact draw date and time is at the top of this page. Every draw uses a cryptographically-seeded RNG whose seed and full eligible ticket list are published (see Past draws / Verify), and is additionally confirmed live by an independent witness.' },
  { q: 'Does the draw still happen if not many tickets are sold?', a: 'Yes — every draw goes ahead on its advertised date, no matter how many tickets sell. Lower sales just mean better odds for the entrants who did take part.' },
  { q: 'Can I take the cash?', a: 'On most prizes, yes. The cash alternative is shown next to the prize.' },
];

const CompetitionPage = () => {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const location = useLocation();
  const [comp, setComp] = useState(null);
  const [quantity, setQuantityState] = useState(3);
  const [user, setUser] = useState(null);
  const [accountSum, setAccountSum] = useState(null);
  const [drawsCount, setDrawsCount] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  // sessionStorage key so quantity + reopen-checkout intent survive the
  // register/login round-trip (account creation is a full-page redirect,
  // so we can't keep pure React state across it).
  const storageKey = `pd_checkout_${slug}`;
  const hydratedRef = useRef(false);

  // Custom setQuantity that also mirrors into sessionStorage so a mid-flow
  // sign-in doesn't lose the user's selection.
  const setQuantity = (v) => {
    setQuantityState(v);
    try {
      const raw = sessionStorage.getItem(storageKey);
      const state = raw ? JSON.parse(raw) : {};
      sessionStorage.setItem(storageKey, JSON.stringify({ ...state, quantity: v }));
    } catch { /* sessionStorage unavailable — non-fatal */ }
  };

  useEffect(() => {
    pastDraws().then((d) => setDrawsCount((d.items || []).length)).catch(() => setDrawsCount(0));
  }, []);

  useEffect(() => {
    getCompetition(slug).then(setComp).catch(() => toast.error('Competition not found'));
  }, [slug]);

  useEffect(() => {
    if (search.get('cancelled')) toast.info('Checkout cancelled. Your entry has not been recorded.');
    // Quick-select handoff from the homepage comp card: ?qty=1 or ?qty=2
    // preselects the quantity and auto-opens the checkout sheet so the
    // visitor lands straight in checkout with the amount already decided.
    const qty = parseInt(search.get('qty') || '', 10);
    if (comp && Number.isFinite(qty) && qty >= 1 && qty <= 100 && !hydratedRef.current) {
      hydratedRef.current = true;
      setQuantity(Math.min(qty, comp.tickets_remaining || qty));
      setSheetOpen(true);
    }
  }, [search, comp]);

  useEffect(() => {
    if (isSignedIn()) {
      whoami().then((r) => setUser(r.user)).catch(() => {
        localStorage.removeItem('pd_user_token');
        localStorage.removeItem('pd_user');
        setUser(null);
      });
      accountSummary().then(setAccountSum).catch(() => setAccountSum(null));
    }
  }, []);

  // Rehydrate persisted state (quantity + reopen-checkout intent) on mount.
  // Runs once, after `comp` is loaded so we can clamp quantity to remaining.
  useEffect(() => {
    if (hydratedRef.current || !comp) return;
    hydratedRef.current = true;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return;
      const state = JSON.parse(raw);
      if (typeof state.quantity === 'number') {
        const max = Math.min(100, comp.tickets_remaining || 0);
        setQuantityState(Math.max(1, Math.min(max || 100, state.quantity)));
      }
      // Only auto-reopen if the user is now signed in (i.e. they came back
      // from register/login mid-flow). Otherwise we'd nag on any refresh.
      if (state.reopenCheckout && isSignedIn()) {
        setSheetOpen(true);
        sessionStorage.setItem(storageKey, JSON.stringify({ ...state, reopenCheckout: false }));
      }
    } catch { /* corrupted json — ignore */ }
  }, [comp, storageKey]);

  // Called right before we navigate away to register/login so we can restore
  // both the quantity AND the fact that the sheet was open when they return.
  const persistCheckoutIntent = () => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({ quantity, reopenCheckout: true }));
    } catch { /* ignore */ }
  };

  // Derive Stage 3/4 blocked reason locally — no extra fetch.
  const blockedReason = useMemo(() => {
    if (!user) return null;
    const nowMs = Date.now();
    if (user.closed_at) return 'Your account has been closed.';
    if (user.suspended_until && new Date(user.suspended_until).getTime() > nowMs) {
      return `Your account is suspended until ${new Date(user.suspended_until).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}.`;
    }
    if (user.pause_until && new Date(user.pause_until).getTime() > nowMs) {
      return `Your account is paused until ${new Date(user.pause_until).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}.`;
    }
    if (accountSum?.credit_card_cap?.at_cap) {
      return 'You\'ve reached your £250 monthly spend cap. It will reset at the start of next month.';
    }
    return null;
  }, [user, accountSum]);

  // Handler for both the sticky-bar CTA and the inline selector's checkout
  // button — same code path, so both surfaces open the same modal with the
  // same shared quantity.
  const openCheckout = () => {
    if (!comp) return;
    setSheetOpen(true);
  };

  if (!comp) {
    return (
      <div className="container-pd py-20">
        <div className="font-mono text-sm text-muted-sage">Loading competition…</div>
      </div>
    );
  }

  const soldOut = comp.tickets_remaining <= 0;

  // Immersive backdrop images. Preference order:
  //   1. Dedicated `background_images` uploaded via admin dashboard
  //   2. Gallery images (previous default)
  //   3. hero_image_url (last-resort fallback so the backdrop is never blank)
  // Up to 3 images are used, in order. If fewer than 3 are supplied, the
  // last available image fills the remaining slots so the page always has a
  // full backdrop.
  const bgSources = (() => {
    const dedicated = (comp.background_images || []).filter(Boolean);
    const pool = (dedicated.length > 0 ? dedicated : [...(comp.gallery || []), comp.hero_image_url])
      .filter(Boolean)
      .map(resolveImg);
    if (pool.length === 0) return [];
    return [0, 1, 2].map((i) => pool[i] ?? pool[pool.length - 1]);
  })();
  // Racing-green wash — deep enough that the image reads as pure atmosphere
  // and never competes with the text/cards on top. Tuned darker than the
  // homepage hero (which is decorative) because this backdrop sits behind
  // interactive content (checkout, postal entry, skill Q) that has to be
  // easy to read even in bright sun on a phone.
  const bgOverlay = 'linear-gradient(180deg, rgba(20,37,28,0.90) 0%, rgba(20,37,28,0.82) 50%, rgba(20,37,28,0.94) 100%)';

  return (
    <>
      <div className="competition-immersive" data-testid="competition-immersive">
        {/* Sticky image stack. `aria-hidden` because the images are decorative
            depth — the meaningful hero photo is described elsewhere. */}
        <div className="comp-bg-stack" aria-hidden="true">
          {bgSources.map((src, i) => (
            <div
              key={i}
              className="comp-bg-slot"
              data-testid={`comp-bg-slot-${i}`}
              style={{ backgroundImage: `${bgOverlay}, url("${src}")` }}
            />
          ))}
        </div>

        <div className="competition-content">
      {/* HERO — compact, image-integrated. Title/subtitle overlay the sticky
       * background image directly at the top of the page (no giant empty
       * spacer above the fold). The dark racing-green wash applied to
       * .comp-bg-slot keeps the copy legible; a small in-hero gradient
       * strengthens legibility on the bottom third where the quick-facts
       * chips sit.
       *
       * Everything the hero renders (category, title, subtitle, cash alt)
       * comes straight from the same admin-editable fields we had before —
       * no data-shape changes. */}
      <section className="relative" data-testid="competition-hero" data-no-reveal>
        <div className="container-pd pt-6 md:pt-12 pb-8 md:pb-12">
          <div className="font-mono text-[10px] text-[color:var(--pd-accent-yellow)] uppercase tracking-[0.2em] mb-3">{comp.category}</div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-7xl leading-[0.95] md:leading-[0.9]" data-testid="competition-title">{comp.title}</h1>
          {comp.subtitle ? (
            <p className="mt-3 md:mt-4 text-base md:text-lg text-white/85 max-w-2xl">{comp.subtitle}</p>
          ) : null}
          {comp.cash_alternative_gbp ? (
            <div className="mt-2 text-sm text-white/70">Or take <span className="text-[color:var(--pd-accent-yellow)] font-mono">{formatGBP(comp.cash_alternative_gbp)}</span> cash.</div>
          ) : null}

          {/* Immediate quick-facts chips — no more empty top-of-page. */}
          <div className="mt-5 md:mt-7 grid grid-cols-3 gap-2 md:gap-3 max-w-2xl" data-testid="hero-quick-facts">
            {[
              { l: 'Ticket price', v: formatGBP(comp.ticket_price_gbp) },
              { l: 'Draw date', v: formatDateShort(comp.draw_date) },
              { l: 'Tickets', v: formatNumber(comp.total_tickets) },
            ].map((k) => (
              <div key={k.l} className="comp-card border border-pd rounded-xl px-3 py-2 md:px-4 md:py-3">
                <div className="font-mono text-[9px] md:text-[10px] text-muted-sage uppercase tracking-wider">{k.l}</div>
                <div className="font-display text-base md:text-lg mt-0.5 md:mt-1 leading-tight">{k.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="container-pd mt-2 md:mt-4 grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
        {/* LEFT column — reordered so users see the gallery, urgency signals
         * and the "what you win" bullets much sooner. Ticket selection
         * happens either through the sticky bottom bar (always visible) or
         * the inline selector further down. */}
        <div className="space-y-5 md:space-y-6">
          {/* Urgency first: countdown + progress + guarantee, in one card. */}
          <div className="comp-card border border-pd rounded-2xl p-5 md:p-6">
            <div className="font-mono text-[10px] text-muted-sage uppercase mb-2">Draw closes in</div>
            <CountdownTimer isoDate={comp.draw_date} size="lg" dataTestidPrefix="comp-countdown" />
            <div className="mt-5">
              <ProgressBar sold={comp.tickets_sold} total={comp.total_tickets} soldOut={soldOut} />
              <div className="mt-3 font-mono text-xs text-muted-sage">{formatNumber(comp.tickets_remaining)} tickets remaining</div>
              <div className="mt-3 guarantee-box" data-testid="comp-guarantee-box">
                Draw goes ahead on <strong>{formatDateShort(comp.draw_date)}</strong>, no matter how many tickets sell. Lower sales mean better odds for you.
              </div>
              {drawsCount !== null && drawsCount > 0 ? (
                <Link
                  to="/past-draws"
                  className="mt-2 inline-flex items-center gap-1 text-xs text-muted-sage hover:text-[color:var(--pd-accent-yellow)]"
                  data-testid="past-draws-link"
                >
                  {drawsCount} previous draw{drawsCount === 1 ? '' : 's'}, all completed as advertised →
                </Link>
              ) : null}
            </div>
          </div>

          {/* Gallery — moved up so the visuals of the actual prize land
              well before the fold on desktop and after one short scroll on
              mobile. Data source unchanged: comp.gallery[] from admin. */}
          {comp.gallery?.length ? (
            <CompetitionGallery images={comp.gallery} />
          ) : null}

          {/* What's in the prize — the admin-authored paragraph
              (`prize_description`) is the intro; the admin-authored bullets
              (`details_bullets`) are rendered as an explicit "what's
              included" checklist with brass-gold ticks. Both fields remain
              fully editable from the admin dashboard. */}
          {(comp.prize_description || comp.details_bullets?.length) ? (
            <div className="comp-card border border-pd rounded-2xl p-5 md:p-6" data-testid="comp-prize-card">
              <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-widest">What you win</div>
              <h2 className="font-display text-2xl md:text-3xl mt-1.5 leading-tight">The prize, in full.</h2>

              {comp.prize_description ? (
                <p className="mt-3 md:mt-4 text-sm md:text-base leading-relaxed text-white/90" data-testid="comp-prize-description">
                  {comp.prize_description}
                </p>
              ) : null}

              {comp.details_bullets?.length ? (
                <>
                  {comp.prize_description ? (
                    <div className="mt-5 md:mt-6 mb-3 md:mb-4 h-px bg-[color:var(--pd-border)]" aria-hidden="true" />
                  ) : null}
                  <div className="font-mono text-[10px] text-muted-sage uppercase tracking-widest mb-3">What's included</div>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2.5 md:gap-y-3" data-testid="comp-prize-bullets">
                    {comp.details_bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-sm leading-snug" data-testid="comp-prize-bullet">
                        <span className="mt-0.5 shrink-0 w-4 h-4 rounded-full bg-[color:var(--pd-gold)]/15 border border-[color:var(--pd-gold)]/45 flex items-center justify-center">
                          <Check size={11} strokeWidth={2.4} className="text-[color:var(--pd-gold)]" aria-hidden="true" />
                        </span>
                        <span className="text-white/90">{b}</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>
          ) : null}

          <TrustStrip />

          {comp.status === 'LIVE' && !soldOut ? (
            <TicketSelector
              price={comp.ticket_price_gbp}
              quantity={quantity}
              setQuantity={setQuantity}
              maxQty={Math.min(100, comp.tickets_remaining)}
              onCheckout={openCheckout}
            />
          ) : null}
        </div>

        {/* RIGHT: free postal entry + compliance disclosures. Skill Q, account
            step, and payment CTA now live in the CheckoutSheet (opened from
            the sticky bottom bar and the inline TicketSelector). */}
        <div>
          <div className="lg:sticky lg:top-24 space-y-5">
            {comp.status === 'COMING_SOON' ? (
              <div className="comp-card border-2 border-[color:var(--pd-gold)]/60 rounded-2xl p-6 space-y-4" data-testid="coming-soon-panel">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--pd-gold)]">Coming soon</div>
                <h3 className="font-display text-2xl leading-tight">This competition isn&rsquo;t open yet.</h3>
                <p className="text-sm text-muted-sage leading-relaxed">
                  We&rsquo;re finalising the details. Ticket sales and the free postal entry route will open shortly.
                  {comp.go_live_at ? (
                    <>
                      {' '}Scheduled to go live on{' '}
                      <span className="text-[color:var(--pd-text)]">{new Date(comp.go_live_at).toLocaleString('en-GB', { dateStyle: 'long', timeStyle: 'short' })}</span>.
                    </>
                  ) : null}
                </p>
                <button
                  disabled
                  className="w-full bg-[color:var(--pd-gold)]/25 text-[color:var(--pd-gold)] border-2 border-[color:var(--pd-gold)]/40 px-4 py-4 rounded-xl font-medium cursor-not-allowed"
                  data-testid="coming-soon-disabled-cta"
                >
                  Coming soon &mdash; not yet open
                </button>
                <p className="text-[11px] text-muted-sage text-center">
                  Free postal entry also opens when this competition goes live &mdash; details will appear here at that moment.
                </p>
              </div>
            ) : comp.status === 'SELECTING_WINNER' ? (
              <div className="comp-card border-2 border-[color:var(--pd-accent-orange)]/60 rounded-2xl p-6 space-y-4" data-testid="selecting-winner-panel">
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-[color:var(--pd-accent-orange)]" />
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--pd-accent-orange)]">Selecting winner</div>
                </div>
                <h3 className="font-display text-2xl leading-tight">Entries have closed.</h3>
                <p className="text-sm text-muted-sage leading-relaxed">
                  The deadline for this competition has passed. Our team is preparing the live, independently-witnessed draw. The winning ticket &mdash; along with the seed and full eligible ticket list used to compute it &mdash; will be published on this page as soon as the draw is complete.
                </p>
                <button
                  disabled
                  className="w-full bg-[color:var(--pd-accent-orange)]/20 text-[color:var(--pd-accent-orange)] border-2 border-[color:var(--pd-accent-orange)]/40 px-4 py-4 rounded-xl font-medium cursor-not-allowed"
                  data-testid="selecting-winner-disabled-cta"
                >
                  Entries closed &mdash; draw in progress
                </button>
                <p className="text-[11px] text-muted-sage text-center">
                  Already entered? Sit tight &mdash; if your ticket wins we&rsquo;ll contact you by email and phone within 24 hours of the draw.
                </p>
              </div>
            ) : (comp.status === 'DRAWN' || comp.status === 'WINNER_ANNOUNCED') ? (
              <div className="comp-card border-2 border-[color:var(--pd-accent-yellow)]/60 rounded-2xl p-6 space-y-4" data-testid="drawn-panel">
                <div className="flex items-center gap-2">
                  <Trophy size={14} className="text-[color:var(--pd-accent-yellow)]" />
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--pd-accent-yellow)]">Winner drawn</div>
                </div>
                <h3 className="font-display text-2xl leading-tight">This competition has been won.</h3>
                {comp.winner_ticket_number ? (
                  <p className="text-sm text-muted-sage leading-relaxed">
                    Winning ticket: <span className="font-mono text-[color:var(--pd-accent-yellow)]">{comp.winner_ticket_number}</span>.
                    {comp.status === 'DRAWN' ? ' The winner is currently being contacted \u2014 we\u2019ll publish their full story once confirmed.' : ''}
                  </p>
                ) : (
                  <p className="text-sm text-muted-sage leading-relaxed">
                    The draw has been completed. The winner is being contacted &mdash; we&rsquo;ll publish their story here once they confirm.
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  <Link to="/winners" className="flex-1 text-center bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-4 py-3 rounded-xl text-sm font-medium" data-testid="drawn-view-winners">See all winners</Link>
                  <Link to="/past-draws" className="flex-1 text-center border border-[color:var(--pd-accent-yellow)] text-[color:var(--pd-accent-yellow)] px-4 py-3 rounded-xl text-sm font-medium" data-testid="drawn-verify-draw">Verify draw</Link>
                </div>
                <p className="text-[11px] text-muted-sage text-center">
                  Every draw&rsquo;s RNG seed and eligible ticket list is public &mdash; anyone can reproduce the result.
                </p>
              </div>
            ) : soldOut ? (
              <div className="bg-[color:var(--pd-accent-orange)] text-[#14251c] rounded-2xl p-4 font-mono text-sm">Sold out. Winner announced after draw.</div>
            ) : (
              <>
                {/* Free postal entry route — Part 6 compliance: stays fully
                    visible on the main page, never hidden behind the paid
                    checkout modal. */}
                <PostalEntryBox competitionId={comp.id} />

                {/* Stage 4 DCMS Code 1.3 disclosures kept on the main page so
                    they're seen BEFORE anyone opens the paid flow. */}
                {comp.instant_win ? (
                  <div className="rounded-2xl border border-[color:var(--pd-gold)]/50 bg-[color:var(--pd-bg)] p-4 text-xs text-[color:var(--pd-text)]" data-testid="instant-win-notice">
                    <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-widest mb-1">Instant-win draw</div>
                    Credit cards are not accepted on instant-win draws under the DCMS Code (clause 1.3). Please pay by debit card only.
                    Any credit-card payment received will be automatically refunded and no tickets will be issued.
                  </div>
                ) : null}
                {user && accountSum?.credit_card_cap && !accountSum.credit_card_cap.at_cap && (accountSum.credit_card_cap.mtd_credit_pence || 0) > 0 ? (
                  <div className="rounded-2xl border border-pd bg-[color:var(--pd-bg)] p-3 text-xs text-muted-sage" data-testid="monthly-cap-headroom-notice">
                    Monthly spend headroom: <span className="text-[color:var(--pd-text)]">£{((accountSum.credit_card_cap.headroom_pence || 0)/100).toFixed(2)}</span> of £250 remaining.
                  </div>
                ) : null}
                {blockedReason ? (
                  <div className="rounded-2xl border border-[color:var(--pd-gold)]/50 bg-[color:var(--pd-bg)] p-4 text-xs text-[color:var(--pd-text)]" data-testid="account-blocked-inline">
                    {blockedReason}{' '}
                    <Link to="/account" className="text-[color:var(--pd-accent-yellow)] underline">Manage in account</Link>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
        </div>{/* /.competition-content */}
      </div>{/* /.competition-immersive */}

      <div className="container-pd mt-16 mb-16">
        <YellowRule className="mb-6" />
        <h3 className="font-display text-3xl md:text-4xl">Frequently asked</h3>
        <div className="mt-6 divide-y divide-[color:var(--pd-border)] border border-pd rounded-2xl overflow-hidden max-w-3xl">
          {FAQS.map((f) => (
            <details key={f.q} className="group" data-testid="comp-faq-item">
              <summary className="list-none cursor-pointer p-4 flex items-center justify-between">
                <span>{f.q}</span>
                <ChevronRight size={16} className="transition-transform group-open:rotate-90" />
              </summary>
              <div className="px-4 pb-4 text-sm text-muted-sage">{f.a}</div>
            </details>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-sage">
          <span>Cryptographically seeded &middot; Independently witnessed &middot; Verify any draw yourself.</span>
          <Link to="/how-we-draw-winners" className="text-[color:var(--pd-accent-yellow)]">How we draw winners →</Link>
        </div>
      </div>

      {/* Bottom spacer so the last of the page content is never hidden behind
          the fixed sticky checkout bar. Height matches the bar (~110px) plus
          safe-area for iOS home indicator. Only present when the checkout
          bar is present (i.e. competition is LIVE and has tickets left). */}
      {comp.status === 'LIVE' && !soldOut ? (
        <div aria-hidden style={{ height: 'calc(120px + env(safe-area-inset-bottom))' }} />
      ) : null}

      {/* Persistent bottom checkout bar. Reads/writes the same `quantity`
          state as the inline TicketSelector -> single source of truth. */}
      {comp.status === 'LIVE' && !soldOut ? (
        <StickyCheckoutBar
          price={comp.ticket_price_gbp}
          quantity={quantity}
          setQuantity={setQuantity}
          maxQty={Math.min(100, comp.tickets_remaining)}
          onCheckout={openCheckout}
          disabled={!!blockedReason}
        />
      ) : null}

      {/* Skill Q -> Account -> Pay modal. Opened from BOTH the sticky bar
          and the inline TicketSelector, so both surfaces trigger the identical
          flow. Dismissing keeps quantity intact via shared parent state. */}
      <CheckoutSheet
        open={sheetOpen}
        onOpenChange={(next) => {
          setSheetOpen(next);
          if (next) persistCheckoutIntent();
        }}
        competition={comp}
        quantity={quantity}
        user={user}
        blockedReason={blockedReason}
        returnTo={location.pathname}
      />
    </>
  );
};

export default CompetitionPage;
